import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Chip,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DataTable from '../../../sharedModules/dataTable';
import PageHeader from '../../../sharedModules/layout/PageHeader';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { useCollectionsLocation } from '../../shared/useCollectionsLocation';
import { formatDollar } from '../../clients/utils/format';
import { Statement } from '../model';
import { statementsQueryKey, useStatements } from '../hooks/useStatements';
import { useUpdateStatementEnabled } from '../hooks/useUpdateStatementEnabled';
import { useUpdateStatementAttachInvoices } from '../hooks/useUpdateStatementAttachInvoices';
import { useStatementConfig } from '../config/hooks/useStatementConfig';
import { isResidentialSuppressed } from '../../clients/utils/clientEligibility';
import StatementPdfsModal from '../components/StatementPdfsModal';
import CollectionClientNotesModal from '../../clients/components/CollectionClientNotesModal';

type TriState = 'all' | 'yes' | 'no';

const ROW_CLICK_TAG = '___react-data-table-allow-propagation___';

interface ModalState {
  open: boolean;
  acctNo: string;
  company: string;
}

interface NotesModalState {
  open: boolean;
  clientId: number;
  company: string;
}

const filterRows = (rows: Statement[], company: string, billCode: string, enabled: TriState): Statement[] => {
  const qCompany = company.trim().toLowerCase();
  const qBillCode = billCode.trim().toLowerCase();
  return rows.filter((row) => {
    if (qCompany && !(row.company ?? '').toLowerCase().includes(qCompany)) return false;
    if (qBillCode && !(row.bill_code ?? '').toLowerCase().includes(qBillCode)) return false;
    if (enabled !== 'all' && row.statement_enabled !== (enabled === 'yes')) return false;
    return true;
  });
};

const buildColumns = (
  onPdfsClick: (row: Statement) => void,
  onToggle: (row: Statement, checked: boolean) => void,
  isToggling: boolean,
  includeResidential: boolean,
  onNotesClick: (row: Statement) => void,
  onViewClick: (row: Statement) => void,
  globalAttachInvoices: boolean,
  onToggleAttach: (row: Statement, checked: boolean) => void,
  isTogglingAttach: boolean,
) => [
  {
    name: 'Company',
    selector: (row: Statement) => row.company,
    sortable: true,
    grow: 2,
    cell: (row: Statement) => {
      const residentialSuppressed = isResidentialSuppressed(row.com_or_res, includeResidential);
      const toggleDisabled = isToggling || !row.collections_client_id || residentialSuppressed;
      const tip = residentialSuppressed
        ? 'Home services clients are excluded from scheduled statements for this location. Change this under Client Eligibility in the statement configuration.'
        : '';

      // Tooltip needs a non-disabled wrapper to receive hover events over a disabled Switch.
      const switchControl = (
        <Tooltip title={tip} disableHoverListener={!tip}>
          <span data-tag={ROW_CLICK_TAG}>
            <Switch
              checked={row.statement_enabled}
              onChange={(e) => onToggle(row, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
              disabled={toggleDisabled}
              inputProps={{
                'aria-label': `Statement enabled for ${row.company}`,
                'aria-disabled': toggleDisabled,
              }}
            />
          </span>
        </Tooltip>
      );

      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {switchControl}
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            {row.company}
          </Typography>
        </Box>
      );
    },
  },
  { name: 'Bill Code', selector: (row: Statement) => row.bill_code, sortable: true, width: '120px' },
  {
    name: 'Emailable',
    selector: (row: Statement) => row.emailable_status,
    sortable: true,
    cell: (row: Statement) => {
      if (row.emailable_status === 'no_email') {
        return <Chip label="No Email" color="error" size="small" data-tag={ROW_CLICK_TAG} />;
      }
      return <Chip label="Yes" color="success" size="small" data-tag={ROW_CLICK_TAG} />;
    },
    width: '95px',
  },
  {
    name: 'Total Invoiced',
    selector: (row: Statement) => row.total_invoiced,
    cell: (row: Statement) => (
      <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
        {formatDollar(row.total_invoiced)}
      </Typography>
    ),
    sortable: true,
    width: '140px',
  },
  {
    name: 'Total Balance',
    selector: (row: Statement) => row.total_balance,
    cell: (row: Statement) => (
      <Typography
        variant="body2"
        data-tag={ROW_CLICK_TAG}
        sx={{
          color: row.total_balance > 0 ? 'error.main' : row.total_balance < 0 ? 'info.main' : 'text.primary',
        }}
      >
        {formatDollar(row.total_balance)}
      </Typography>
    ),
    sortable: true,
    width: '140px',
  },
  {
    name: 'Invoices',
    // Sort by missing files (total - matched), so descending puts clients with the most missing invoice PDFs on top.
    selector: (row: Statement) => row.invoice_count - row.matched_pdf_count,
    sortable: true,
    center: true,
    width: '120px',
    cell: (row: Statement) => {
      if (row.invoice_count === 0) {
        return (
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            0
          </Typography>
        );
      }
      if (!row.collections_client_id) {
        return (
          <Typography variant="body2" data-tag={ROW_CLICK_TAG}>
            {`${row.matched_pdf_count} / ${row.invoice_count}`}
          </Typography>
        );
      }
      const allMatched = row.matched_pdf_count === row.invoice_count;
      return (
        <Chip
          icon={<PictureAsPdfIcon />}
          label={`${row.matched_pdf_count} / ${row.invoice_count}`}
          size="small"
          color={allMatched ? 'primary' : 'error'}
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onPdfsClick(row);
          }}
          sx={{ cursor: 'pointer', px: 1, py: 0.5, height: 'auto' }}
          aria-label={`${row.matched_pdf_count} of ${row.invoice_count} invoices have PDFs for ${row.company}`}
        />
      );
    },
  },
  {
    name: 'Invoice PDFs',
    center: true,
    width: '120px',
    cell: (row: Statement) => {
      const effectiveChecked = globalAttachInvoices && row.attach_invoices;
      const disabled = isTogglingAttach || !row.collections_client_id || !globalAttachInvoices;
      const tip = !globalAttachInvoices
        ? 'Turned off because the global invoice-attachment setting is off for this location.'
        : '';

      // Tooltip needs a non-disabled wrapper to receive hover events over a disabled Switch.
      return (
        <Tooltip title={tip} disableHoverListener={!tip}>
          <span data-tag={ROW_CLICK_TAG}>
            <Switch
              checked={effectiveChecked}
              onChange={(e) => onToggleAttach(row, e.target.checked)}
              onClick={(e) => e.stopPropagation()}
              color="primary"
              size="small"
              disabled={disabled}
              inputProps={{
                'aria-label': `Invoice attachments for ${row.company}`,
                'aria-disabled': disabled,
              }}
            />
          </span>
        </Tooltip>
      );
    },
  },
  {
    name: 'Notes',
    selector: (row: Statement) => row.notes_count,
    sortable: true,
    center: true,
    cell: (row: Statement) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onNotesClick(row);
        }}
        title="View notes"
        aria-label={`${row.notes_count} notes for ${row.company}`}
      >
        <Badge
          badgeContent={row.notes_count}
          color="primary"
          max={99}
          showZero
          sx={{
            '& .MuiBadge-badge': {
              minWidth: 22,
              height: 20,
              padding: '0 6px',
              fontSize: '0.75rem',
              lineHeight: '20px',
              whiteSpace: 'nowrap',
            },
          }}
        >
          <ChatBubbleOutlineIcon fontSize="small" />
        </Badge>
      </IconButton>
    ),
    width: '100px',
  },
  {
    name: '',
    width: '50px',
    center: true,
    cell: (row: Statement) => (
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onViewClick(row);
        }}
        title="View statement details"
        aria-label={`View details for ${row.company}`}
      >
        <VisibilityIcon fontSize="small" />
      </IconButton>
    ),
  },
];

const StatementListPage: React.FC = () => {
  const history = useHistory();
  const location = useCollectionsLocation();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useStatements(location ?? '', !!location);
  const { updateStatementEnabled, isLoading: isToggling } = useUpdateStatementEnabled();
  const { data: configData } = useStatementConfig(location ?? '', !!location);
  const globalAttachInvoices = configData?.data?.attach_invoices ?? true;
  const includeResidential = configData?.data?.include_residential ?? false;
  const { updateStatementAttachInvoices, isLoading: isTogglingAttach } = useUpdateStatementAttachInvoices();
  const { showErrorSnackBar, showSuccessSnackBar } = useContext(SnackBarContext);

  const [statements, setStatements] = useState<Statement[]>([]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [billCodeFilter, setBillCodeFilter] = useState('');
  const [enabledFilter, setEnabledFilter] = useState<TriState>('all');
  const [modal, setModal] = useState<ModalState>({ open: false, acctNo: '', company: '' });
  const [notesModal, setNotesModal] = useState<NotesModalState>({ open: false, clientId: 0, company: '' });

  useEffect(() => {
    if (data?.data) setStatements(data.data);
  }, [data]);

  const filtered = useMemo(
    () => filterRows(statements, companyFilter, billCodeFilter, enabledFilter),
    [statements, companyFilter, billCodeFilter, enabledFilter],
  );

  if (!location) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <PageHeader mainTitle="Statements - Clients" titleIcon={<DescriptionIcon />} />
        </Grid>
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Alert severity="info">No Collections location is assigned to this account.</Alert>
        </Grid>
      </Grid>
    );
  }

  const handleClearFilters = () => {
    setCompanyFilter('');
    setBillCodeFilter('');
    setEnabledFilter('all');
  };

  const handleToggle = async (row: Statement, enabled: boolean) => {
    if (!row.collections_client_id) {
      showErrorSnackBar?.('Statement not available for update.');
      return;
    }

    const previous = row.statement_enabled;
    setStatements((prev) => prev.map((s) => (s.acct_no === row.acct_no ? { ...s, statement_enabled: enabled } : s)));
    try {
      await updateStatementEnabled({ acctNo: row.acct_no, location, statementEnabled: enabled });
      showSuccessSnackBar?.(`Statement ${enabled ? 'enabled' : 'disabled'} for ${row.company}.`);
    } catch (error: any) {
      setStatements((prev) => prev.map((s) => (s.acct_no === row.acct_no ? { ...s, statement_enabled: previous } : s)));
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update statement status.');
    }
  };

  const handleToggleAttach = async (row: Statement, attach: boolean) => {
    if (!row.collections_client_id) {
      showErrorSnackBar?.('Statement not available for update.');
      return;
    }

    const previous = row.attach_invoices;
    setStatements((prev) => prev.map((s) => (s.acct_no === row.acct_no ? { ...s, attach_invoices: attach } : s)));
    try {
      await updateStatementAttachInvoices({ acctNo: row.acct_no, location, attachInvoices: attach });
      showSuccessSnackBar?.(`Invoice attachments ${attach ? 'enabled' : 'disabled'} for ${row.company}.`);
    } catch (error: any) {
      setStatements((prev) => prev.map((s) => (s.acct_no === row.acct_no ? { ...s, attach_invoices: previous } : s)));
      const serverMessage = error?.response?.data?.errors?.[0] || error?.response?.data?.error;
      showErrorSnackBar?.(serverMessage || 'Failed to update invoice attachments.');
    }
  };

  const handlePdfsClick = (row: Statement) => {
    setModal({ open: true, acctNo: row.acct_no, company: row.company });
  };

  const handleNotesClick = (row: Statement) => {
    if (!row.collections_client_id) return;
    setNotesModal({ open: true, clientId: row.collections_client_id, company: row.company });
  };

  const handleViewStatement = (row: Statement) => {
    history.push(`/collections/statements/${encodeURIComponent(row.acct_no)}`);
  };

  const handlePdfChanged = () => {
    void queryClient.invalidateQueries({ queryKey: statementsQueryKey(location) });
  };

  const tableData = isError ? [] : filtered;

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageHeader mainTitle="Statements - Clients" titleIcon={<DescriptionIcon />} />
      </Grid>
      {isError && (
        <Grid item xs={12} sx={{ mt: 1 }}>
          <Alert severity="error">Failed to load statements.</Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={3}>
            <TextField
              label="Company Name"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="Bill Code"
              value={billCodeFilter}
              onChange={(e) => setBillCodeFilter(e.target.value)}
              size="small"
              fullWidth
              variant="outlined"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="statements-filter-enabled">Active</InputLabel>
              <Select
                labelId="statements-filter-enabled"
                label="Active"
                value={enabledFilter}
                onChange={(e) => setEnabledFilter(e.target.value as TriState)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button variant="outlined" size="small" onClick={handleClearFilters} sx={{ mb: 0.25 }}>
              Clear filters
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} sx={{ mt: 2.5 }}>
        <Card>
          <DataTable
            columns={buildColumns(
              handlePdfsClick,
              handleToggle,
              isToggling,
              includeResidential,
              handleNotesClick,
              handleViewStatement,
              globalAttachInvoices,
              handleToggleAttach,
              isTogglingAttach,
            )}
            data={tableData}
            progressPending={isLoading}
            pagination
            paginationPerPage={25}
            paginationRowsPerPageOptions={[25, 50, 100]}
            defaultSortField="total_balance"
            defaultSortAsc={false}
            highlightOnHover
            pointerOnHover
            striped
            noHeader
            onRowClicked={handleViewStatement}
          />
        </Card>
      </Grid>
      <StatementPdfsModal
        open={modal.open}
        onClose={() => setModal({ open: false, acctNo: '', company: '' })}
        acctNo={modal.acctNo}
        company={modal.company}
        location={location}
        onPdfChanged={handlePdfChanged}
      />
      <CollectionClientNotesModal
        open={notesModal.open}
        onClose={() => setNotesModal({ open: false, clientId: 0, company: '' })}
        clientId={notesModal.clientId}
        company={notesModal.company}
        onNotesChanged={() => {
          void queryClient.invalidateQueries({ queryKey: statementsQueryKey(location) });
        }}
      />
    </Grid>
  );
};

export default StatementListPage;
